import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PushContext = createContext(null);

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function PushProvider({ children }) {
  const { user } = useAuth();
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const lastNotifCheck = useRef(null);
  const lastNotifIds = useRef(new Set());

  // Check support
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setPushSupported(supported);
    if (supported) {
      setPushPermission(Notification.permission);
    }
  }, []);

  // Register service worker & check subscription
  useEffect(() => {
    if (!pushSupported || !user) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        const sub = await reg.pushManager.getSubscription();
        setPushSubscribed(!!sub);
        // Show banner if not subscribed and hasn't been dismissed
        if (!sub && Notification.permission === 'default') {
          const dismissed = localStorage.getItem('push_banner_dismissed');
          if (!dismissed) {
            setTimeout(() => setShowBanner(true), 3000);
          }
        }
      } catch (e) {
        console.error('SW registration failed:', e);
      }
    })();
  }, [pushSupported, user]);

  // In-app notification polling (toast fallback)
  useEffect(() => {
    if (!user) return;
    const pollNotifications = async () => {
      try {
        const { data } = await axios.get(`${API}/notifications?limit=5`, { withCredentials: true });
        const now = new Date();
        for (const notif of data) {
          if (!notif.read && !lastNotifIds.current.has(notif.id)) {
            const created = new Date(notif.created_at);
            if (lastNotifCheck.current && created > lastNotifCheck.current) {
              toast(notif.title, { description: notif.message, duration: 5000 });
            }
            lastNotifIds.current.add(notif.id);
          }
        }
        lastNotifCheck.current = now;
      } catch {}
    };
    // Initial load (don't show toasts)
    lastNotifCheck.current = new Date();
    axios.get(`${API}/notifications?limit=5`, { withCredentials: true })
      .then(r => r.data.forEach(n => lastNotifIds.current.add(n.id)))
      .catch(() => {});
    const interval = setInterval(pollNotifications, 15000);
    return () => clearInterval(interval);
  }, [user]);

  const subscribeToPush = useCallback(async () => {
    if (!pushSupported) return false;
    try {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      if (permission !== 'granted') return false;

      const reg = await navigator.serviceWorker.ready;
      // Get VAPID public key
      const { data: vapidData } = await axios.get(`${API}/push/vapid-public-key`);
      const applicationServerKey = urlBase64ToUint8Array(vapidData.public_key);

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });

      // Send subscription to backend
      await axios.post(`${API}/push/subscribe`, {
        subscription: subscription.toJSON()
      }, { withCredentials: true });

      setPushSubscribed(true);
      setShowBanner(false);
      return true;
    } catch (e) {
      console.error('Push subscribe failed:', e);
      return false;
    }
  }, [pushSupported]);

  const unsubscribeFromPush = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios.post(`${API}/push/unsubscribe`, {
          endpoint: sub.endpoint
        }, { withCredentials: true });
        await sub.unsubscribe();
      }
      setPushSubscribed(false);
    } catch (e) {
      console.error('Push unsubscribe failed:', e);
    }
  }, []);

  const dismissBanner = useCallback(() => {
    setShowBanner(false);
    localStorage.setItem('push_banner_dismissed', 'true');
  }, []);

  return (
    <PushContext.Provider value={{
      pushSupported, pushPermission, pushSubscribed,
      showBanner, subscribeToPush, unsubscribeFromPush, dismissBanner
    }}>
      {children}
    </PushContext.Provider>
  );
}

export function usePush() {
  return useContext(PushContext);
}
